import React from 'react';
import './TeamBuilder.css';

const TeamBuilder = () => {
  return (
    <div className="team-builder">
      <div className="team-builder__header">
        <h2>Building Your Team</h2>
        <p>A guide to creating effective competitive teams in Pokémon Champions</p>
      </div>

      <div className="team-builder__content">
        <section className="rental-teams">
          <h3>Getting Started with Rental Teams</h3>
          <p>
            Building a competitive team from scratch can be challenging.
            Rental teams are a great way to learn the meta and understand team synergy before committing your VP to building your own.
          </p>

          <div className="rental-resources">
            <h4>Where to Find Rental Teams:</h4>
            <div className="resource-cards">
              <div className="resource-card">
                <h5>Victory Road VGC</h5>
                <p>A trusted resource for competitive rental teams with detailed team reports and usage guides, now covering Pokémon Champions Regulation M-A.</p>
                <a href="https://victoryroad.pro/champions-regulations/" target="_blank" rel="noopener noreferrer">Visit Website</a>
              </div>

              <div className="resource-card">
                <h5>Pokémon Champions In-Game</h5>
                <p>Pokémon Champions has a built-in rental team feature. You can borrow teams from top-ranked players directly in the game without spending VP.</p>
              </div>

              <div className="resource-card">
                <h5>Twitter/X #VGC</h5>
                <p>Follow competitive players who regularly share their successful team compositions.</p>
                <p className="note">Search for #PokemonChampions or #VGC2026</p>
              </div>
            </div>
          </div>
        </section>

        <section className="team-format-note">
          <h3>Pokémon Champions Team Format</h3>
          <div className="building-tips">
            <div className="tip tip--highlight">
              <h4>Register 6, Bring 4</h4>
              <ul>
                <li>You register a team of 6 Pokémon before the match.</li>
                <li>Before battle, <strong>both players see each other's full team of 6</strong>.</li>
                <li>Each player then selects 4 of their 6 to bring into the actual battle.</li>
                <li>This makes team preview and flexible team building extremely important.</li>
              </ul>
            </div>
            <div className="tip tip--highlight">
              <h4>One Gimmick Per Battle (Omni Ring)</h4>
              <ul>
                <li>Each team can only use one battle gimmick per match.</li>
                <li>In Regulation M-A, that means one <strong>Mega Evolution</strong> per battle.</li>
                <li>Choose your Mega strategically — it defines a large part of your game plan.</li>
                <li>Your opponent can see your Mega Stone holder in team preview.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="team-building-guide">
          <h3>Team Building Fundamentals</h3>

          <div className="building-tips">
            <div className="tip">
              <h4>1. Core Synergy</h4>
              <ul>
                <li>Start with a strong core of 2–3 Pokémon that work well together</li>
                <li>Consider type synergies and coverage</li>
                <li>Mix offensive and defensive Pokémon</li>
                <li>Your Mega Evolution pick often defines your entire team archetype</li>
              </ul>
            </div>

            <div className="tip">
              <h4>2. Role Coverage</h4>
              <ul>
                <li>Include different roles: Sweepers, Tanks, Support</li>
                <li>Have answers to common Reg M-A threats (Incineroar, Amoonguss, Mega Gardevoir)</li>
                <li>Consider speed control options — Tailwind and Trick Room are both viable</li>
              </ul>
            </div>

            <div className="tip">
              <h4>3. Move Coverage</h4>
              <ul>
                <li>Ensure your team can hit all types for at least neutral damage</li>
                <li>Include status moves and support options</li>
                <li>Think about how your moves interact with your Mega's new ability</li>
              </ul>
            </div>

            <div className="tip">
              <h4>4. Item Distribution</h4>
              <ul>
                <li>Your Mega Pokémon must hold its Mega Stone — plan other items around that</li>
                <li>Avoid duplicate items on non-Mega teammates</li>
                <li>Match items to Pokémon roles (Life Orb, Choice items, Assault Vest, etc.)</li>
              </ul>
            </div>

            <div className="tip">
              <h4>5. Test and Refine</h4>
              <ul>
                <li>Use Casual mode in Pokémon Champions to test without risking rank</li>
                <li>Private battles let you practice with friends</li>
                <li>Keep track of common losses and adjust accordingly</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="common-mistakes">
          <h3>Common Team Building Mistakes to Avoid</h3>
          <ul>
            <li>Having too many Pokémon with similar weaknesses</li>
            <li>Forgetting that the opponent sees your full 6 in preview — avoid obvious one-trick teams</li>
            <li>Not having a plan for when your Mega pick is countered</li>
            <li>Lacking speed control options (Tailwind, Thunder Wave, Trick Room)</li>
            <li>Over-relying on a single strategy</li>
            <li>Ignoring support moves in favor of all attacks</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TeamBuilder;
